//* Libraries imports
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

//* Axios Import
import { publicApi } from "../../../services/apis/configs/apiClient";

//* Schema imports
import {
  changeRecoveryPasswordSchema,
  changeRecoveryPasswordSuccessSchema,
  type ChangeRecoveryPasswordRequest,
} from "../../../schemas/common/recovery-password/recorvery-password";

//* Store imports
import { authSlice } from "../../../services/stores/auth/slice";

export type ChangeRecoveryPasswordParams = ChangeRecoveryPasswordRequest;

async function changeRecoveryPassword(params: ChangeRecoveryPasswordParams) {
  try {
    const validatedData = changeRecoveryPasswordSchema.parse(params);
    
    const { token, password, password_confirmation } = validatedData;
    const url = "/student_portal/public/confirm_password_reset";
    
    const response = await publicApi.post(url, { 
      token, 
      password, 
      password_confirmation 
    });
    
    
    return changeRecoveryPasswordSuccessSchema.parse(response);

  } catch (err: any) {
    // Handle Zod validation errors specifically
    if (err.name === 'ZodError') {
      console.error("Zod validation error:", err.errors);
      console.error("Data that failed validation:", err.input);
      throw new Error("Formato de resposta inválido do servidor");
    }
    
    const errorMessage = err.response?.data?.message || err.response?.data?.error?.message;
    const validationErrors = err.response?.data?.errors;
    
    if (validationErrors) {
      // Handle validation errors
      const errorMessages = [];
      
      if (validationErrors.password) {
        errorMessages.push(...validationErrors.password);
      }
      
      if (validationErrors.password_confirmation) {
        errorMessages.push(...validationErrors.password_confirmation);
      }
      
      if (validationErrors.token) {
        errorMessages.push(...validationErrors.token);
      }
      
      if (errorMessages.length > 0) {
        throw new Error(errorMessages.join(', '));
      }
    }
    
    if (errorMessage) {
      if (errorMessage === 'Token de recuperação inválido ou expirado') {
        throw new Error("Token de recuperação inválido ou expirado");
      }
      
      if (errorMessage === 'Os dados fornecidos são inválidos') {
        throw new Error("Os dados fornecidos são inválidos");
      }
      
      if (errorMessage === 'Nova senha é obrigatória') {
        throw new Error("Nova senha é obrigatória");
      }
      
      if (errorMessage === 'Confirmação da senha não confere') {
        throw new Error("Confirmação da senha não confere");
      }
      
      throw new Error(errorMessage);
    }
    
    console.error("Erro ao alterar senha de recuperação:", err);
    throw new Error("Erro interno do servidor. Tente novamente mais tarde.");
  }
}

export function useChangeRecoveryPassword() {
  const dispatch = useDispatch();
  
  return useMutation({
    mutationFn: changeRecoveryPassword,
    onSuccess: (data) => {
      if (data.result && data.data.access.token && data.data.user) {
        dispatch(authSlice.actions.setTokenAndMe({
          token: data.data.access.token,
          me: data.data.user
        }));
      }
    },
  });
}
